/**
 * The reconnect policy shared by every authenticated WebSocket hook
 * (TASK-4987 / BUG-580).
 *
 * Three near-identical copies of this block used to live in
 * `useTrackingWebSocket`, `useNotificationsWebSocket` and `useChatWebSocket`.
 * Patching them one at a time is how this family of defects regrows, so the
 * policy lives here once and the three hooks call into it.
 *
 * ---------------------------------------------------------------------------
 * Two defects this module fixes, both observed on prod on 2026-09-23.
 * ---------------------------------------------------------------------------
 *
 * 1. **An expired token fell into the retry loop forever.** Only 4003 stopped
 *    the reconnect (4001 || 4003 in the chat copy), so a token that had expired
 *    two hours earlier produced 290 connections in 10 minutes from one client,
 *    with nothing on screen to tell the user the page had stopped updating.
 *    `WS_CLOSE_TOKEN_INVALID` (4401) now triggers exactly ONE token refresh and,
 *    if that fails, a terminal dead state.
 *
 * 2. **The exponential backoff never actually grew.** The tracking consumer
 *    calls `await self.accept()` BEFORE `close(...)`, so the handshake SUCCEEDS
 *    and `ws.onopen` fires on every single attempt — and `onopen` reset the
 *    backoff to 1 s. Every cycle therefore scheduled 1000 ms, doubled it to
 *    2000 ms, and was reset again by the next open: the machine-exact 2.00 s
 *    interval in the report. A socket that opens and is immediately closed by
 *    the server has NOT proven usable, so the reset here is gated on the
 *    connection either receiving a message or surviving
 *    {@link MIN_USABLE_CONNECTION_MS}. `notifyOpened` deliberately does not
 *    reset anything by itself.
 *
 * Jitter is applied on top so many tabs, all woken by the same server restart,
 * do not retry in lockstep.
 */

import { refreshAccessToken } from "@/lib/api";

/**
 * WebSocket close codes the backend uses (`core/src/apps/core/ws_close_codes.py`).
 * Mirrored here as a client contract — the handshake refusal has no response
 * body, so the close code is the only channel that carries the reason.
 */
export const WS_CLOSE_NO_TOKEN = 4001;
export const WS_CLOSE_TOKEN_INVALID = 4401;
export const WS_CLOSE_FORBIDDEN = 4003;
export const WS_CLOSE_CHANNEL_LAYER_UNAVAILABLE = 4500;

/** First retry delay. */
export const INITIAL_BACKOFF_MS = 1_000;
/** Ceiling for the exponential backoff. */
export const MAX_BACKOFF_MS = 30_000;

/**
 * How long an open socket must survive before the backoff is allowed to reset.
 *
 * The prod trace shows a socket lifetime of ~45 ms on the failing path (open,
 * then immediately closed by the consumer). Anything that short is a refusal
 * dressed as a success and must NOT be credited as a working connection. A real
 * connection lives for minutes, so the threshold is not delicate.
 */
export const MIN_USABLE_CONNECTION_MS = 5_000;

/** What the policy decided should happen after a close. */
export type ReconnectDecision =
  /** Schedule another attempt after `delayMs`. */
  | { action: "retry"; delayMs: number }
  /** Refresh the access token first, then reconnect once. */
  | { action: "refresh" }
  /** Stop for good; the UI should show a dead state. */
  | { action: "stop"; reason: DeadReason };

/** Why the socket gave up — drives the message the user sees. */
export type DeadReason =
  /** No token, or the tenant is suspended. Nothing to refresh. */
  | "unauthenticated"
  /** This role is not permitted on this socket. */
  | "forbidden"
  /** The token was refreshed (or the refresh failed) and it still did not help. */
  | "tokenExpired";

/**
 * Random source for jitter, injectable so a test can assert the jitter is
 * actually applied against a deterministic sequence rather than a flaky real one.
 */
export type RandomSource = () => number;

/**
 * Applies FULL jitter to a delay: a uniform draw over `[0, delay]`.
 *
 * Full rather than equal jitter because the problem here is synchronisation
 * across tabs, and full jitter spreads a herd the widest. The exponential
 * ceiling still bounds the worst case.
 */
export function jitter(delayMs: number, random: RandomSource): number {
  return Math.round(delayMs * random());
}

/**
 * The mutable reconnect state of ONE socket hook.
 *
 * Deliberately a plain object rather than a class: the hooks hold it in a
 * `useRef` and reset it wholesale on remount.
 */
export interface ReconnectState {
  /** The CURRENT un-jittered delay; doubles per attempt up to the ceiling. */
  backoffMs: number;
  /** Set once a 4401 refresh has been attempted, so it is attempted only once. */
  refreshAttempted: boolean;
  /** `performance`-independent timestamp of the last `onopen`, or null. */
  openedAt: number | null;
  /** True once this connection proved usable (a message, or a long-enough open). */
  proven: boolean;
}

/** A fresh state for a newly-mounted hook. */
export function createReconnectState(): ReconnectState {
  return {
    backoffMs: INITIAL_BACKOFF_MS,
    refreshAttempted: false,
    openedAt: null,
    proven: false,
  };
}

/**
 * Records that the socket opened. Note what this does NOT do: it does not reset
 * the backoff. See defect 2 in the module docstring — an open that the server
 * closes 45 ms later is not evidence of anything.
 */
export function notifyOpened(state: ReconnectState, now: number): void {
  state.openedAt = now;
  state.proven = false;
}

/**
 * Records that a frame arrived. THIS is proof the connection works, so it is
 * what resets the backoff (and re-arms the one-shot token refresh).
 */
export function notifyMessage(state: ReconnectState): void {
  state.proven = true;
  state.backoffMs = INITIAL_BACKOFF_MS;
  state.refreshAttempted = false;
}

/**
 * Decides what to do after a close, and MUTATES `state` accordingly.
 *
 * @param state  The hook's reconnect state.
 * @param code   The WebSocket close code.
 * @param now    Current timestamp in ms (`Date.now()`), for the lifetime check.
 * @param random Jitter source.
 */
export function decideAfterClose(
  state: ReconnectState,
  code: number,
  now: number,
  random: RandomSource,
): ReconnectDecision {
  // --- Terminal codes: a refresh cannot help, so do not loop. -------------
  // 4001 covers both "no token supplied" and "tenant suspended"; the
  // distinction that matters to a client is whether a refresh can help, and for
  // both of these it cannot.
  if (code === WS_CLOSE_NO_TOKEN) {
    return { action: "stop", reason: "unauthenticated" };
  }
  if (code === WS_CLOSE_FORBIDDEN) {
    return { action: "stop", reason: "forbidden" };
  }

  // --- 4401: refresh the access token exactly once. -----------------------
  if (code === WS_CLOSE_TOKEN_INVALID) {
    if (state.refreshAttempted) {
      // Already refreshed once and still rejected: the loop stops here rather
      // than falling back into the retry path. Falling back is the bug.
      return { action: "stop", reason: "tokenExpired" };
    }
    state.refreshAttempted = true;
    return { action: "refresh" };
  }

  // --- Everything else is retryable with backoff. -------------------------
  // The reset is credited ONLY to a connection that proved usable: a frame
  // arrived (`notifyMessage`), or the open survived MIN_USABLE_CONNECTION_MS.
  const survived =
    state.openedAt !== null && now - state.openedAt >= MIN_USABLE_CONNECTION_MS;
  if (state.proven || survived) {
    state.backoffMs = INITIAL_BACKOFF_MS;
    state.refreshAttempted = false;
  }

  const delayMs = jitter(state.backoffMs, random);
  state.backoffMs = Math.min(state.backoffMs * 2, MAX_BACKOFF_MS);
  state.openedAt = null;
  state.proven = false;
  return { action: "retry", delayMs };
}

/**
 * Refreshes the access token for a 4401 reconnect.
 *
 * Delegates to `refreshAccessToken` in `@/lib/api`, whose single-flight promise
 * means three hooks refreshing at the same moment issue ONE HTTP request.
 * Resolves to the new token, or `null` when the refresh failed — a `null` is the
 * dead state, never a reason to retry.
 */
export async function refreshWsToken(): Promise<string | null> {
  try {
    return await refreshAccessToken();
  } catch {
    return null;
  }
}
